<script setup lang="ts">
const props = defineProps<{ post: PostItem }>();

const localePath = useLocalePath();
const { getPostTypeLabel } = usePosts();
const { getPostTypeSlug } = usePostTypes();

const typeLabel = computed(() =>
  props.post.type ? getPostTypeLabel(props.post.type) : undefined
);

const tag = computed(() => props.post.tags?.[0]?.tags_id);

const logo = computed(() => {
  if (
    props.post?.type &&
    [PostType.Professional, PostType.Contractor].includes(props.post.type)
  ) {
    const logo = props.post?.logo;

    if (logo) {
      const logoHeight = logo.height || 28;
      const logoWidth = logo.width || 28;

      const ratio = 40 / logoHeight;

      const height = logoHeight * ratio;
      const width = logoWidth * ratio;

      return {
        ...logo,
        height,
        width,
        sizes: `${logoWidth}px xs:${logoWidth}px sm:${logoWidth}px md:${logoWidth}px lg:${logoWidth}px xl:${logoWidth}px xxl:${logoWidth}px`,
      };
    }
  }

  return null;
});

const parentPost = computed(() => {
  if (props.post?.type && props.post.type === PostType.Inspiration) {
    const parentPost = props.post.parent_post;

    if (
      parentPost?.type &&
      [PostType.Professional, PostType.Contractor].includes(parentPost.type)
    ) {
      return parentPost;
    }
  }

  return null;
});

const parentPostLogo = computed(() => {
  const logo = parentPost.value?.logo;

  if (logo) {
    const logoHeight = logo.height || 28;
    const logoWidth = logo.width || 28;

    const ratio = 40 / logoHeight;

    const height = logoHeight * ratio;
    const width = logoWidth * ratio;

    return {
      ...logo,
      height,
      width,
      sizes: `${logoWidth}px xs:${logoWidth}px sm:${logoWidth}px md:${logoWidth}px lg:${logoWidth}px xl:${logoWidth}px xxl:${logoWidth}px`,
    };
  }

  return null;
});
</script>

<template>
  <div class="post-content-tags px-5 lg:px-0">
    <ul
      class="post-content-tags__wrapper flex items-center justify-between lg:justify-start flex-wrap"
      :class="[logo || parentPostLogo ? 'gap-6' : 'gap-4']"
    >
      <li
        v-if="parentPost?.type && parentPostLogo"
        class="post-content-tags__item post-content-tags__item--logo"
      >
        <NuxtLink
          :to="
            localePath({
              name: 'type-slug',
              params: {
                type: getPostTypeSlug(parentPost.type),
                slug: parentPost.slug,
              },
            })
          "
          :title="
            [parentPost.title, parentPost.subtitle].filter(Boolean).join(' ')
          "
        >
          <NuxtPicture
            provider="directus"
            :src="
              parentPostLogo.id +
              (parentPostLogo.filename_download
                ? '/' + encodeURIComponent(parentPostLogo.filename_download)
                : '')
            "
            :width="parentPostLogo.width"
            :height="parentPostLogo.height"
            :modifiers="{ fit: 'contain' }"
            :alt="parentPostLogo.description || props.post.parent_post?.title"
            :sizes="parentPostLogo.sizes"
            class="block w-auto h-auto max-h-10 max-w-32"
            :img-attrs="{ class: 'block w-auto h-auto max-h-10 max-w-32' }"
            quality="65"
            preload
          />
        </NuxtLink>
      </li>

      <li
        v-else-if="logo"
        class="post-content-tags__item post-content-tags__item--logo"
      >
        <NuxtPicture
          provider="directus"
          :src="
            logo.id +
            (logo.filename_download
              ? '/' + encodeURIComponent(logo.filename_download)
              : '')
          "
          :width="logo.width"
          :height="logo.height"
          :modifiers="{ fit: 'contain' }"
          :alt="logo.description || props.post.title"
          :sizes="logo.sizes"
          class="block w-auto h-auto max-h-10 max-w-32"
          :img-attrs="{ class: 'block w-auto h-auto max-h-10 max-w-32' }"
          quality="65"
          preload
        />
      </li>

      <li
        v-if="typeLabel"
        class="post-content-tags__item post-content-tags__item--type"
      >
        <PostContentTagsItem :tag="{ title: typeLabel }" :color="post.type" />
      </li>

      <li
        v-if="tag"
        class="post-content-tags__item post-content-tags__item--tag"
      >
        <PostContentTagsItem v-bind="{ tag }" />
      </li>
    </ul>
  </div>
</template>
